import { generateMeshes } from '../geometry/generateMeshes';
import { fibonacciHemisphere } from '../lasers/fibonacciHemisphere';
import { Vec3 } from '../math/Vec3';
import { meanNearestNeighbourDistance } from '../math/pointCloudMetrics';
import { FilamentRaycaster } from '../optics/FilamentRaycaster';
import { performance } from '../platform/performance';
import { sampleProjectionFigure } from '../projection/figures';
import { buildOpticalVoxels } from '../voxelization/buildOpticalVoxels';
import type {
  LaserEmitter,
  OpticalVoxel,
  RayRequest,
  RayResult,
  SimulationConfig,
  ProjectionQualityStatistics,
  SimulationSnapshot,
  SimulationStatistics,
  VoxelCoverageReport,
} from './types';

export interface ProjectionFrame {
  rotation: Vec3;
  pivot: Vec3;
  rays: RayResult[];
  statistics: SimulationStatistics;
}

interface IndexedFixedRay {
  index: number;
  ray: RayResult;
}

interface FigureRaySelection {
  rays: RayResult[];
  requestedPoints: number;
  mappingErrors: number[];
}

/**
 * Keeps animated figure matching proportional to the requested detail instead
 * of scanning the complete physical laser bank for every projected point.
 * The cell edge is also the maximum accepted mapping error, so the nearest
 * valid candidate can only live in the current cell or one of its neighbours.
 */
class FixedRaySpatialIndex {
  private readonly cells = new Map<string, IndexedFixedRay[]>();
  private readonly cellSize: number;
  private readonly maximumDistanceSquared: number;
  readonly size: number;

  constructor(rays: readonly RayResult[], tolerance: number) {
    const accessible = rays.filter((ray) => ray.targetAccessible && ray.firstIntersection);
    this.size = accessible.length;
    if (!Number.isFinite(tolerance) || tolerance <= 0) throw new Error('Invalid projection tolerance');
    const cellSize = tolerance;
    this.maximumDistanceSquared = cellSize * cellSize;

    accessible.forEach((ray, index) => {
      const coordinates = this.cellCoordinates(ray.target, cellSize);
      const key = this.cellKey(coordinates.x, coordinates.y, coordinates.z);
      const cell = this.cells.get(key);
      const indexedRay = { index, ray };
      if (cell) cell.push(indexedRay);
      else this.cells.set(key, [indexedRay]);
    });
    this.cellSize = cellSize;
  }

  closestUnused(point: Vec3, used: ReadonlySet<number>): IndexedFixedRay | null {
    const center = this.cellCoordinates(point, this.cellSize);
    let closest: IndexedFixedRay | null = null;
    let closestDistanceSquared = this.maximumDistanceSquared;

    for (let xOffset = -1; xOffset <= 1; xOffset += 1) {
      for (let yOffset = -1; yOffset <= 1; yOffset += 1) {
        for (let zOffset = -1; zOffset <= 1; zOffset += 1) {
          const cell = this.cells.get(this.cellKey(
            center.x + xOffset,
            center.y + yOffset,
            center.z + zOffset,
          ));
          if (!cell) continue;
          for (const candidate of cell) {
            if (used.has(candidate.index)) continue;
            const dx = point.x - candidate.ray.target.x;
            const dy = point.y - candidate.ray.target.y;
            const dz = point.z - candidate.ray.target.z;
            const distanceSquared = dx * dx + dy * dy + dz * dz;
            if (
              distanceSquared > closestDistanceSquared ||
              (distanceSquared === closestDistanceSquared &&
                closest !== null && candidate.index > closest.index)
            ) continue;
            closest = candidate;
            closestDistanceSquared = distanceSquared;
          }
        }
      }
    }
    return closest;
  }

  private cellCoordinates(point: Vec3, cellSize: number): Vec3 {
    return new Vec3(
      Math.floor(point.x / cellSize),
      Math.floor(point.y / cellSize),
      Math.floor(point.z / cellSize),
    );
  }

  private cellKey(x: number, y: number, z: number): string {
    return `${x}:${y}:${z}`;
  }
}

export function auditVoxelCoverage(
  voxels: readonly OpticalVoxel[],
  rays: readonly RayResult[],
  addedLaserCount = 0,
): VoxelCoverageReport {
  const illuminatedVoxelIds = new Set(
    rays.filter((ray) => ray.targetAccessible && ray.firstIntersection).map((ray) => ray.voxelId),
  );
  const missingVoxelIds = voxels
    .filter((voxel) => !illuminatedVoxelIds.has(voxel.id))
    .map((voxel) => voxel.id);
  return {
    testedVoxels: voxels.length,
    coveredVoxels: voxels.length - missingVoxelIds.length,
    missingVoxelIds,
    addedLaserCount,
    complete: voxels.length > 0 && missingVoxelIds.length === 0,
  };
}

function calculateStatistics(
  meshCount: number,
  filamentCount: number,
  rays: readonly RayResult[],
  simulationTimeMs: number,
  projectionQuality: ProjectionQualityStatistics | null = null,
): SimulationStatistics {
  const successfulRays = rays.filter((ray) => ray.targetAccessible).length;
  const blockedRays = rays.filter((ray) => ray.blocked).length;
  const missedRays = rays.length - successfulRays - blockedRays;
  return {
    meshCount,
    filamentCount,
    logicalRays: rays.length,
    successfulRays,
    blockedRays,
    missedRays,
    occlusionPercentage: rays.length > 0 ? (blockedRays / rays.length) * 100 : 0,
    simulationTimeMs,
    projectionQuality,
  };
}

function calculateProjectionQuality(selection: FigureRaySelection, tolerance: number): ProjectionQualityStatistics {
  const sortedErrors = [...selection.mappingErrors].sort((first, second) => first - second);
  const squaredErrorTotal = sortedErrors.reduce((total, error) => total + error * error, 0);
  const p95Index = Math.max(0, Math.ceil(sortedErrors.length * 0.95) - 1);
  return {
    requestedPoints: selection.requestedPoints,
    activatedPoints: selection.rays.length,
    unmatchedPoints: selection.requestedPoints - selection.rays.length,
    figureCoveragePercentage: selection.requestedPoints > 0
      ? selection.rays.length / selection.requestedPoints * 100 : null,
    mappingTolerance: tolerance,
    mappingErrorRms: sortedErrors.length > 0
      ? Math.sqrt(squaredErrorTotal / sortedErrors.length)
      : null,
    mappingErrorP95: sortedErrors[p95Index] ?? null,
    mappingErrorMax: sortedErrors.at(-1) ?? null,
    meanActiveVoxelSeparation: meanNearestNeighbourDistance(
      selection.rays.map((ray) => ray.target),
    ),
  };
}

function fixedRayRequest(emitter: LaserEmitter, voxel: OpticalVoxel): RayRequest {
  return {
    id: voxel.id,
    voxelId: voxel.id,
    emitterId: emitter.id,
    origin: emitter.position,
    direction: voxel.position.sub(emitter.position).normalize(),
    target: voxel.position,
    targetMeshId: voxel.meshId,
    targetFilamentId: voxel.filamentId,
  };
}

function traceFixedVoxelBank(
  voxels: readonly OpticalVoxel[],
  emitters: readonly LaserEmitter[],
  raycaster: FilamentRaycaster,
): RayResult[] {
  const emitterById = new Map(emitters.map((emitter) => [emitter.id, emitter]));
  return voxels.flatMap<RayResult>((voxel): RayResult[] => {
    const emitter = emitterById.get(voxel.emitterId);
    if (!emitter) return [];
    const nominalRequest = fixedRayRequest(emitter, voxel);
    const firstIntersection = raycaster.cast(nominalRequest.origin, nominalRequest.direction);
    if (!firstIntersection) {
      return [{
        ...nominalRequest,
        firstIntersection: null,
        targetAccessible: false,
        blocked: false,
      }];
    }
    // Calibration promotes the immutable first physical hit to this laser's
    // actual voxel. A nearer filament is not a movable-beam failure: it is the
    // one point that this fixed emitter can physically illuminate.
    const calibratedVoxel: OpticalVoxel = {
      id: voxel.id,
      emitterId: emitter.id,
      position: firstIntersection.position,
      meshId: firstIntersection.meshId,
      filamentId: firstIntersection.filamentId,
    };
    const request = fixedRayRequest(emitter, calibratedVoxel);
    return [{
      ...request,
      firstIntersection,
      targetAccessible: true,
      blocked: false,
    }];
  });
}

function replacementEmitter(
  id: number,
  voxel: OpticalVoxel,
  config: SimulationConfig,
  attempt: number,
): LaserEmitter {
  const floorY = -config.cubeSize.y / 2;
  const angle = attempt * Math.PI * (3 - Math.sqrt(5));
  const radial = config.hemisphereRadius * Math.min(0.88, 0.18 + attempt * 0.055);
  const vertical = Math.sqrt(Math.max(0, config.hemisphereRadius ** 2 - radial ** 2));
  return {
    id,
    position: new Vec3(
      voxel.position.x + Math.cos(angle) * radial,
      floorY - vertical,
      voxel.position.z + Math.sin(angle) * radial,
    ),
    wavelengthNm: 532,
  };
}

export function repairUncoveredVoxels(
  config: SimulationConfig,
  designedVoxels: readonly OpticalVoxel[],
  initialEmitters: readonly LaserEmitter[],
  initialRays: readonly RayResult[],
  raycaster: FilamentRaycaster,
): { emitters: LaserEmitter[]; voxels: OpticalVoxel[]; rays: RayResult[]; addedLaserCount: number } {
  const emitters = [...initialEmitters];
  const raysByVoxel = new Map(initialRays.map((ray) => [ray.voxelId, ray]));
  let nextEmitterId = emitters.reduce((maximum, emitter) => Math.max(maximum, emitter.id), -1) + 1;
  let addedLaserCount = 0;

  for (const designedVoxel of designedVoxels) {
    if (raysByVoxel.get(designedVoxel.id)?.targetAccessible) continue;
    for (let attempt = 0; attempt < 24; attempt += 1) {
      const emitter = replacementEmitter(nextEmitterId, designedVoxel, config, attempt);
      const request = fixedRayRequest(emitter, { ...designedVoxel, emitterId: emitter.id });
      const firstIntersection = raycaster.cast(request.origin, request.direction);
      if (!firstIntersection) continue;
      const calibratedVoxel: OpticalVoxel = {
        id: designedVoxel.id,
        emitterId: emitter.id,
        position: firstIntersection.position,
        meshId: firstIntersection.meshId,
        filamentId: firstIntersection.filamentId,
      };
      const calibratedRequest = fixedRayRequest(emitter, calibratedVoxel);
      emitters.push(emitter);
      raysByVoxel.set(designedVoxel.id, {
        ...calibratedRequest,
        firstIntersection,
        targetAccessible: true,
        blocked: false,
      });
      nextEmitterId += 1;
      addedLaserCount += 1;
      break;
    }
  }

  const rays = designedVoxels.flatMap((voxel) => {
    const ray = raysByVoxel.get(voxel.id);
    return ray ? [ray] : [];
  });
  const voxels = rays.map((ray) => ({
    id: ray.voxelId,
    emitterId: ray.emitterId,
    position: ray.target,
    meshId: ray.targetMeshId,
    filamentId: ray.targetFilamentId,
  }));
  return { emitters, voxels, rays, addedLaserCount };
}

export function projectionSampleCount(fixedVoxelCount: number): number {
  if (fixedVoxelCount <= 0) return 0;
  return Math.min(fixedVoxelCount, Math.max(48, Math.round(fixedVoxelCount * 0.55)));
}

function selectFixedRaysForFigure(
  config: SimulationConfig,
  spatialIndex: FixedRaySpatialIndex,
  rotation: Vec3,
  pivot: Vec3,
): FigureRaySelection {
  const desiredPoints = sampleProjectionFigure(
    config.projectionFigure,
    config.cubeSize,
    projectionSampleCount(config.logicalLaserCount),
    rotation,
    pivot,
  );
  const selected = new Set<number>();
  const rays: RayResult[] = [];
  const mappingErrors: number[] = [];

  for (const point of desiredPoints) {
    const closest = spatialIndex.closestUnused(point, selected);
    if (!closest) continue;
    selected.add(closest.index);
    rays.push(closest.ray);
    mappingErrors.push(point.distanceTo(closest.ray.target));
  }
  return { rays, requestedPoints: desiredPoints.length, mappingErrors };
}

/**
 * Animates only by choosing which immutable laser/voxel pairs are on. Origins,
 * directions and targets are copied from the fixed physical bank and never
 * recalculated between frames.
 */
export class ProjectionSequence {
  private readonly config: SimulationConfig;
  private readonly spatialIndex: FixedRaySpatialIndex;
  private readonly meshCount: number;
  private readonly filamentCount: number;
  private lastActiveLaserCount = 0;

  constructor(snapshot: SimulationSnapshot) {
    this.config = snapshot.config;
    this.meshCount = snapshot.meshes.length;
    this.filamentCount = snapshot.meshes.reduce((total, mesh) => total + mesh.segments.length, 0);
    this.spatialIndex = new FixedRaySpatialIndex(snapshot.rays, snapshot.config.projectionTolerance);
  }

  get activeLaserCount(): number {
    return this.lastActiveLaserCount;
  }

  frame(rotation: Vec3, _frameIndex = 0, pivot = new Vec3()): ProjectionFrame {
    const startedAt = performance.now();
    const selection = selectFixedRaysForFigure(this.config, this.spatialIndex, rotation, pivot);
    const rays = selection.rays;
    const projectionQuality = calculateProjectionQuality(selection, this.config.projectionTolerance);
    this.lastActiveLaserCount = rays.length;
    const simulationTimeMs = performance.now() - startedAt;
    return {
      rotation,
      pivot,
      rays,
      statistics: calculateStatistics(
        this.meshCount,
        this.filamentCount,
        rays,
        simulationTimeMs,
        projectionQuality,
      ),
    };
  }
}

export function runSimulation(config: SimulationConfig): SimulationSnapshot {
  const startedAt = performance.now();
  const generated = generateMeshes(config);
  const segments = generated.meshes.flatMap((mesh) => mesh.segments);
  const emitters = fibonacciHemisphere({
    count: config.logicalLaserCount,
    radius: config.hemisphereRadius,
    minimumAngle: config.laserMinAngle,
    maximumAngle: config.laserMaxAngle,
    rimY: -config.cubeSize.y / 2,
  });
  const designedVoxels = buildOpticalVoxels(segments, emitters);

  if (segments.length === 0) {
    const simulationTimeMs = performance.now() - startedAt;
    return {
      config,
      meshes: generated.meshes,
      emitters,
      voxels: designedVoxels,
      rays: [],
      diagnostics: generated.diagnostics,
      statistics: calculateStatistics(generated.meshes.length, 0, [], simulationTimeMs),
      coverage: auditVoxelCoverage(designedVoxels, []),
    };
  }

  const cellSize = Math.max(config.filamentRadius * 8, Math.min(config.cubeSize.x, config.cubeSize.y) / 28);
  const raycaster = new FilamentRaycaster(segments, config.cubeSize, cellSize);
  const initialRays = traceFixedVoxelBank(designedVoxels, emitters, raycaster);
  const repaired = repairUncoveredVoxels(config, designedVoxels, emitters, initialRays, raycaster);
  const coverage = auditVoxelCoverage(repaired.voxels, repaired.rays, repaired.addedLaserCount);
  const simulationTimeMs = performance.now() - startedAt;

  return {
    config,
    meshes: generated.meshes,
    emitters: repaired.emitters,
    voxels: repaired.voxels,
    rays: repaired.rays,
    diagnostics: generated.diagnostics,
    statistics: calculateStatistics(generated.meshes.length, segments.length, repaired.rays, simulationTimeMs),
    coverage,
  };
}
